import { Breadcrumb } from '@/app/components/Breadcrumb'
import { Footer } from '@/app/components/Footer'
import {
  INotificationStatus,
  useNotificationContext,
} from '@/app/components/Notification'
import { useCheckout } from '@/app/hooks/data-hooks/useCheckout'
import {
  getPaymentOptionsQueryOptions,
  usePaymentOptions,
} from '@/app/hooks/data-hooks/usePaymentOptions'
import { useCohortHelpers } from '@/app/hooks/useCohortHelpers'
import { EXTERNAL_LINKS, ROUTES } from '@/app/lib/constants'
import { Header } from '@/app/modules/shared/components/Header'
import { useAuth } from '@/app/providers/Auth/Auth'
import { ICategory } from '@/types/category'
import { ICourse } from '@/types/course'
import { IFinancialTerm } from '@/types/financial-term'
import { PaymentMethod, PaymentMethodMode } from '@/types/payment'
import { yupResolver } from '@hookform/resolvers/yup'
import * as Sentry from '@sentry/nextjs'
import Cookies from 'js-cookie'
import { useTranslations } from 'next-intl'
import Head from 'next/head'
import { useRouter } from 'next/router'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useQueryClient } from 'react-query'
import resolveConfig from 'tailwindcss/resolveConfig'
import { v4 as uuidV4 } from 'uuid'
import * as yup from 'yup'
import myConfig from '../../../../../tailwind.config'
import { sendCourseViewEvent } from '../../shared/lib/analytics'
import AlreadyAppliedAlert from '../components/AlreadyAppliedAlert'
import { CourseBanner } from '../components/CourseBanner'
import { CourseCohorts } from '../components/CourseCohorts'
import { CourseCohortSelectorModal } from '../components/CourseCohortSelectorModal'
import { CourseContent } from '../components/CourseContent'
import { CourseEducationProvider } from '../components/CourseEducationProvider'
import { CourseFAQ } from '../components/CourseFAQ'
import { CourseFeatures } from '../components/CourseFeatures'
import { CourseHeader } from '../components/CourseHeader'
import { CourseInstructors } from '../components/CourseInstructors'
import { CourseMotivation } from '../components/CourseMotivation'
import { CoursePrerequisites } from '../components/CoursePrerequisites'
import { CoursePriceBar } from '../components/CoursePriceBar'
import { CourseTargets } from '../components/CourseTargets'
import { CourseTestimonials } from '../components/CourseTestimonials'
import { EnrolmentCard } from '../components/EnrolmentCard'
import { ISAPaymentOptionAd } from '../components/EnrolmentCard/ISAPaymentOptionAd'
import { LearningDescription } from '../components/LearningDescription'
import { useCourseApplicationStatus } from '../hooks/useCourseApplicationStatus'

type CoursePageProps = {
  course: ICourse
}

export type CoursePageFormValues = {
  selectedCohortId: string
}

export const CoursePage = ({ course }: CoursePageProps) => {
  const queryClient = useQueryClient()
  const { theme } = resolveConfig(myConfig)
  const { isCohortActive, hasValidCost } = useCohortHelpers()
  const user = useAuth()
  const t = useTranslations('CoursePage')
  const [isCohortSelectionModalOpen, setCohortSelectionModalOpen] =
    useState(false)
  const { mutateAsync: checkout, isLoading: isCheckoutLoading } = useCheckout()
  const router = useRouter()
  const sessionId = useMemo(() => uuidV4(), [])
  const { setMessage } = useNotificationContext()

  const {
    _id = '',
    name,
    courseMotivation,
    targets = [],
    educationProvider,
    categories = [],
    language,
    hasCertificate,
    summary,
    learningAreas = [],
    level,
    cohorts: allCohorts = [],
    programmeStructure,
    testimonials = [],
    instructors = [],
    learningDescription,
    brochureUrl,
    prerequisites = [],
    url,
    imageUrl,
    financialTerms,
  } = course

  const [mainFinancialTerm] = (financialTerms ?? []) as IFinancialTerm[]

  if (!Array.isArray(allCohorts) || allCohorts?.length === 0) {
    throw new Error(`No cohorts found for course ${_id}`)
  }

  const courseId = _id
  const activeCohorts = [...allCohorts.filter((c) => !!isCohortActive(c))].sort(
    (a, b) =>
      new Date(a.startDate!).getTime() - new Date(b.startDate!).getTime()
  )
  const activeCohortsWithValidCost = activeCohorts.filter(hasValidCost)

  const hasActiveCohortsWithValidCost =
    Array.isArray(activeCohortsWithValidCost) &&
    activeCohortsWithValidCost.length > 0

  const [category] = categories

  const { userAlreadyApplied, isLoading: isLoadingApplicationStatus } =
    useCourseApplicationStatus({
      courseInfo: { _id },
    })

  const form = useForm<CoursePageFormValues>({
    values: {
      selectedCohortId:
        (router.query.selectedCohortId as string) ??
        activeCohorts?.[0]?._id ??
        '',
    },
    disabled:
      isLoadingApplicationStatus ||
      user?.isLoading ||
      isCheckoutLoading ||
      userAlreadyApplied,
    resolver: yupResolver(
      yup.object({
        selectedCohortId: yup.string().uuid().required(),
      })
    ),
  })

  const selectedCohortId = form.watch('selectedCohortId')

  const {
    data: { data: paymentMethodConfigurations = [] } = {},
    isLoading: isLoadingPaymentMethodConfigurations,
    isRefetching: isRefetchingPaymentMethodConfigurations,
  } = usePaymentOptions(
    {
      courseId: course._id!,
      cohortId: selectedCohortId,
    },
    {
      enabled: !!course._id && !!selectedCohortId && !!(user && user?.userId),
      retry: false,
    }
  )

  const redirectCourseApplyToSignUp = useCallback(() => {
    return router.push({
      pathname: ROUTES.signup,
      query: {
        redirectTo: `${ROUTES.course.replace(':id', courseId)}?selectedCohortId=${selectedCohortId}`,
      },
    })
  }, [router, courseId, selectedCohortId])

  const isLoading =
    isLoadingApplicationStatus ||
    user?.isLoading ||
    isLoadingPaymentMethodConfigurations ||
    isRefetchingPaymentMethodConfigurations ||
    isCheckoutLoading

  const onSubmit = useCallback(
    async (data: CoursePageFormValues) => {
      try {
        if (isLoading) {
          return
        }

        if (!user.isAuthenticated) {
          return await redirectCourseApplyToSignUp()
        }

        if (paymentMethodConfigurations.length === 0) {
          throw new Error(
            `No payment method configuration is available for course ${_id} and cohort ${selectedCohortId}`
          )
        }

        const freePaymentMethodConfiguration = paymentMethodConfigurations.find(
          (config) => config.type === PaymentMethod.Free
        )

        const noAgreementPaymentMethodConfiguration =
          paymentMethodConfigurations.find(
            (config) => config.type === PaymentMethod.NoAgreement
          )

        if (freePaymentMethodConfiguration?.isAvailable) {
          const { data: { redirectUrl } = {} } = await checkout({
            courseId,
            cohortId: data.selectedCohortId,
            paymentOptions: [
              {
                type: PaymentMethod.Free,
                amount: 0,
                mode: PaymentMethodMode.Upfront,
              },
            ],
          })

          if (redirectUrl) {
            return await router.push(redirectUrl)
          }
        } else if (noAgreementPaymentMethodConfiguration?.isAvailable) {
          const { data: { redirectUrl } = {} } = await checkout({
            courseId,
            cohortId: data.selectedCohortId,
            paymentOptions: [
              {
                type: PaymentMethod.NoAgreement,
                amount: 0,
                mode: PaymentMethodMode.Upfront,
              },
            ],
          })

          if (redirectUrl) {
            if (redirectUrl === course.url) {
              Cookies.set(sessionId, course._id!)
              return await router
                .push(
                  `${ROUTES.courseRedirect}?courseId=${course._id}&sessionId=${sessionId}`
                )
                .then(() => true)
            }

            return await router.push(redirectUrl)
          }
        } else {
          return await router.push(
            `${ROUTES.checkout}?courseId=${courseId}&selectedCohortId=${data.selectedCohortId}`
          )
        }
      } catch (error) {
        Sentry.captureException(error)
        setMessage(t('tryAgain'), INotificationStatus.ERROR)
      }
    },
    [
      courseId,
      _id,
      isLoading,
      selectedCohortId,
      paymentMethodConfigurations,
      redirectCourseApplyToSignUp,
      user,
      router,
      checkout,
      course._id,
      course.url,
      sessionId,
      setMessage,
      t,
    ]
  )

  const paymentOptionQueryParamsForAllCohorts = useMemo(() => {
    return allCohorts.map((cohort) => {
      return {
        courseId: courseId,
        cohortId: cohort._id,
      }
    })
  }, [allCohorts, courseId])

  useEffect(() => {
    if (!user.isAuthenticated) {
      return
    }

    paymentOptionQueryParamsForAllCohorts.forEach((params) => {
      queryClient.prefetchQuery(
        getPaymentOptionsQueryOptions(params, {
          enabled: !!user.isAuthenticated,
        })
      )
    })
  }, [paymentOptionQueryParamsForAllCohorts, queryClient, user.isAuthenticated])

  useEffect(() => {
    // force form validation to properly set the isValid flag
    // @see: https://github.com/orgs/react-hook-form/discussions/10267
    form.trigger()
  }, [form])

  useEffect(() => {
    sendCourseViewEvent(course)
  }, [course])

  return (
    <FormProvider {...form}>
      <Head>
        <title>{`${name} | ${t('courses_marketplace')} | Mydra`}</title>
        <style>
          {`
              @media (max-width: ${theme?.screens?.['md']}) {
                :root {
                  --hs-beacon-v-offset: 200px !important;
                }
              }
            `}
        </style>
      </Head>
      <Header />

      <main className="overflow-x-clip">
        <AlreadyAppliedAlert courseInfo={{ _id }} className="w-full" />
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className="container relative mt-1 grid grid-cols-4 gap-x-4 md:mt-12 md:grid-cols-12 lg:gap-x-8">
            <div className="col-span-5 col-start-9 col-end-[-1] hidden gap-4 md:flex md:flex-col">
              <div className="sticky right-0 top-4 mx-auto flex w-full max-w-[400px] flex-col gap-4">
                <EnrolmentCard
                  activeCohorts={activeCohorts}
                  isLoading={isLoading}
                  courseInfo={{
                    name,
                    cohorts: activeCohorts,
                    _id,
                    educationProvider,
                    categories,
                    url,
                    imageUrl,
                    financialTerms,
                  }}
                  disabled={
                    isLoading || userAlreadyApplied || !user?.isAuthenticated
                  }
                />
              </div>
            </div>
            <div className="col-span-full md:col-span-7 md:row-start-1">
              <Breadcrumb
                className="w-full"
                items={[
                  { path: '/search', label: t('all_courses') },
                  ...((category as ICategory)?.name
                    ? [
                        {
                          path: `/search?category=${(category as ICategory)._id}`,
                          label: (category as ICategory).name,
                        },
                      ]
                    : []),
                ]}
              />

              <CourseBanner
                className="-mx-5 mt-3 overflow-hidden md:hidden md:rounded-lg"
                courseInfo={{ _id, categories, educationProvider, imageUrl }}
              />

              <CourseHeader
                className="mt-5 md:mx-0 md:mt-1"
                course={{
                  name,
                  summary,
                  learningAreas,
                  level,
                  cohorts: activeCohorts,
                }}
              />

              <LearningDescription
                className="mt-8 md:mx-0 md:mt-10 lg:mt-8"
                courseInfo={{ learningDescription, brochureUrl }}
              />

              <CourseFeatures
                className="mt-9 md:mx-0 md:mt-12 lg:w-[85%]"
                courseInfo={{
                  cohorts: activeCohorts,
                  language,
                  hasCertificate,
                }}
              />

              <CourseTargets
                className="clear-both mt-10 md:mt-12"
                courseInfo={{ targets }}
              />

              <CourseEducationProvider
                className="col-span-full mt-10 md:mt-14"
                courseInfo={{ educationProvider }}
              />
            </div>
          </div>
          <div className="container relative grid grid-cols-4 gap-x-4 md:grid-cols-12 lg:gap-x-8">
            {(!!courseMotivation ||
              (Array.isArray(prerequisites) && prerequisites.length > 0)) && (
              // TODO: extract this to a component
              <div className="full-bleed col-span-full mt-8 w-full bg-mydra-light-beige md:mt-16 md:gap-y-16 md:py-8">
                <div className="!md:px-0 container grid grid-cols-4 md:grid-cols-12">
                  <CourseMotivation
                    className="col-span-full max-md:px-8 lg:col-span-10 lg:col-start-2"
                    courseInfo={{
                      _id: courseId,
                      educationProvider,
                      courseMotivation,
                      categories,
                      imageUrl,
                    }}
                  />
                  <CoursePrerequisites
                    className="col-span-full max-md:px-8 lg:col-span-10 lg:col-start-2"
                    courseInfo={{ prerequisites }}
                  />
                </div>
              </div>
            )}

            <CourseContent
              className="col-span-full mt-8 md:mt-24 lg:col-span-8 lg:col-start-3"
              course={{ programmeStructure }}
            />

            <CourseTestimonials
              className="col-span-full mt-8 md:col-span-12 md:mt-20"
              courseInfo={{ testimonials }}
            />

            <CourseInstructors
              className="col-span-full lg:col-span-8 lg:col-start-3"
              courseInfo={{ instructors }}
            />

            <CourseCohorts
              className="full-bleed col-span-full w-full max-md:px-8"
              courseInfo={{
                _id,
                cohorts: activeCohorts,
                educationProvider,
              }}
              isLoading={isLoading}
              disabled={
                isLoading ||
                !hasActiveCohortsWithValidCost ||
                userAlreadyApplied ||
                !user?.isAuthenticated
              }
              onApply={async (selectedCohortId) => {
                form.setValue('selectedCohortId', selectedCohortId)
                await form.handleSubmit(onSubmit)()
              }}
            />

            <CourseFAQ
              className="max-md:full-bleed col-span-full w-full lg:col-span-8 lg:col-start-3"
              onGetInTouch={() => {
                window.open(
                  EXTERNAL_LINKS.courseSuggestionForm,
                  '_blank',
                  'noopener'
                )
              }}
              course={{ financialTerms, name, educationProvider }}
            />
          </div>

          <div className="sticky bottom-0 left-0 w-full md:hidden">
            <ISAPaymentOptionAd
              financialTerm={mainFinancialTerm}
              variant="compact"
              className="border-t bg-light-gray p-4"
            />
            <CoursePriceBar
              isLoading={isLoading}
              disabled={
                isLoading ||
                userAlreadyApplied ||
                !hasActiveCohortsWithValidCost
              }
              courseInfo={{
                _id,
                educationProvider,
                cohorts: activeCohorts,
                financialTerms,
              }}
              onApply={async (selectedCohortId) => {
                form.setValue('selectedCohortId', selectedCohortId)
                await form.handleSubmit(onSubmit)()
              }}
              onCohortSelection={() => {
                setCohortSelectionModalOpen(true)
              }}
            />
          </div>
        </form>
      </main>
      <Footer />

      <CourseCohortSelectorModal
        className="md:hidden"
        isOpen={isCohortSelectionModalOpen}
        onClose={() => setCohortSelectionModalOpen(false)}
        onSubmit={async ({ selectedCohortId }) => {
          form.setValue('selectedCohortId', selectedCohortId)
          await form.handleSubmit(onSubmit)()
          setCohortSelectionModalOpen(false)
        }}
        courseInfo={{ cohorts: activeCohorts }}
      />
    </FormProvider>
  )
}
